// ---------- pointillist alphabet ----------
// The symbol isn't a shape, it's a color; a single stroke, the disc.
//   0     empty cell = bare paper (the highlights)
//   1..9  nine pure pigments
// No black, no gray: per divisionism, dark values and broken tones come from the
// optical mixing of neighboring pure pigments. That's the role played by
// Floyd-Steinberg dithering, applied to the three channels.

import { rgbOf } from '../ink';
import type { Alphabet } from './types';

/** Exportés pour l'avatar de l'écran « Moi » et la médaille Prisme (src/profile.ts) : le joueur
 *  est peint avec les couleurs du jeu, pas avec une palette inventée à côté. */
export const PIGMENTS = [
  { css: '#f2ca35', name: 'jaune de cadmium' },
  { css: '#8cb62c', name: 'vert de vessie' },
  { css: '#2ca272', name: 'vert émeraude' },
  { css: '#2e8ec4', name: 'bleu céruléum' },
  { css: '#1f2f7a', name: 'outremer' },
  { css: '#4a2a6b', name: 'violet de cobalt' },
  { css: '#b23a63', name: 'laque de garance' },
  { css: '#d63f2e', name: 'vermillon' },
  { css: '#e8802c', name: 'orange de cadmium' },
];

export interface Pigment {
  r: number;
  g: number;
  b: number;
  css: string;
  name: string;
}

let palette: Pigment[] | null = null;
/** paper luminance: threshold beyond which the conversion leaves the cell bare */
let paperLum = 0;

export function paperLuminance(): number {
  getPalette();
  return paperLum;
}

/** Named channels, from whichever form the charter's token comes back in — see rgbOf(). */
function chan(col: string) {
  const [r, g, b] = rgbOf(col);
  return { r, g, b };
}

export function getPalette(): Pigment[] {
  if (palette) return palette;
  const p = chan(
    getComputedStyle(document.documentElement).getPropertyValue('--paper').trim() || '#fbf6ec',
  );
  // the canvas is transparent: under the discs you see the paper darkened by 4% from
  // the .sheetwrap veil. That's the tint "empty cell" needs to target.
  palette = [
    {
      r: p.r * 0.96,
      g: p.g * 0.96,
      b: p.b * 0.96,
      css: 'transparent',
      name: 'papier nu',
    },
    ...PIGMENTS.map((q) => ({ ...chan(q.css), css: q.css, name: q.name })),
  ];
  const p0 = palette[0];
  paperLum = 0.299 * p0.r + 0.587 * p0.g + 0.114 * p0.b;
  return palette;
}

export const pointille: Alphabet = {
  id: 'pointille',
  sheetMode: 'single',
  label: 'Pointillisme',
  symbolName: 'pigment',
  selfColored: true,
  helpText:
    'Divisionnisme : <b>neuf pigments purs</b> et le papier nu, aucun noir ni gris. Les ' +
    'valeurs sombres et les tons rompus naissent du <b>mélange optique</b> de disques ' +
    "voisins — recule d'un mètre pour que l'œil fasse le mélange.",

  aspect: () => 1,

  // Explicit rather than falling back to NUMERIC: the fallback spans 1..NB_CODES-1, and
  // NB_CODES is shared with alphabets that use more codes (see alphabets/index.ts) — this
  // alphabet only ever produces 1..9 (nine pigments).
  paletteOrder: [1, 2, 3, 4, 5, 6, 7, 8, 9],

  codeName: (code) => getPalette()[code].name,

  draw(ctx, x, y, w, h, code) {
    if (!code) return; // 0: bare paper, nothing to draw
    const s = Math.min(w, h);
    ctx.fillStyle = getPalette()[code].css;
    ctx.beginPath();
    // 0.46: the discs almost touch and let the paper breathe between them
    ctx.arc(x + w / 2, y + h / 2, s * 0.46, 0, 7);
    ctx.fill();
  },
};
